import type {
  UpstreamModelDiscoveryConfig,
  UpstreamModelListAuth,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useT } from '../i18n/index.ts';
import { FormField, Input, Select } from '../ui/index.ts';
import type { SelectOption } from '../ui/index.ts';

/**
 * Shared model-discovery form projection (issue #1658 part B).
 *
 * Both write surfaces — the new-upstream wizard step 1 and the detail
 * page's configuration card — edit the same draft shape. Draft semantics
 * follow `EgressConfigEditor`: every field keeps its last value while the
 * operator switches modes, so flipping the mode select never wipes the
 * `list` fields; only the fields valid for the selected mode are emitted
 * (`modelDiscoveryFromDraft`) and PATCH/POST carries one atomic
 * `model_discovery` object.
 */

export type ModelDiscoveryMode = UpstreamModelDiscoveryConfig['mode'];

export const MODEL_DISCOVERY_MODES: readonly ModelDiscoveryMode[] = [
  'compatible',
  'list',
  'manual',
];

const LIST_AUTH_VALUES: readonly UpstreamModelListAuth[] = [
  'anthropic',
  'bearer',
  'google-api-key',
];

export interface ModelDiscoveryDraft {
  readonly mode: ModelDiscoveryMode;
  readonly path: string;
  /** `.`-separated JSON object keys, rendered as one line of text. */
  readonly responsePathText: string;
  readonly auth: UpstreamModelListAuth;
}

export const EMPTY_MODEL_DISCOVERY_DRAFT: ModelDiscoveryDraft = {
  mode: 'compatible',
  path: '',
  responsePathText: '',
  auth: 'bearer',
};

export function draftFromModelDiscovery(
  config: UpstreamModelDiscoveryConfig | undefined,
): ModelDiscoveryDraft {
  if (config === undefined || config.mode === 'compatible') {
    return EMPTY_MODEL_DISCOVERY_DRAFT;
  }
  if (config.mode === 'manual') {
    return { ...EMPTY_MODEL_DISCOVERY_DRAFT, mode: 'manual' };
  }
  return {
    mode: 'list',
    path: config.path,
    responsePathText: config.response_path.join('.'),
    auth: config.auth,
  };
}

function responsePathFromText(text: string): readonly string[] {
  return text
    .split('.')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);
}

export function modelDiscoveryFromDraft(
  draft: ModelDiscoveryDraft,
): UpstreamModelDiscoveryConfig {
  if (draft.mode === 'list') {
    return {
      mode: 'list',
      path: draft.path.trim(),
      response_path: responsePathFromText(draft.responsePathText),
      auth: draft.auth,
    };
  }
  return { mode: draft.mode };
}

type Translate = ReturnType<typeof useT>;

/**
 * Client-side pre-validation mirroring the gateway's `list` constraints
 * (`apps/gateway-worker/src/upstreams/model-discovery-config.ts`) so the
 * common mistakes fail before a request leaves the console. Same-origin
 * resolution stays server-side — the endpoint URL may be edited in the
 * same session and the gateway is the authority.
 */
export function validateModelDiscoveryDraft(
  draft: ModelDiscoveryDraft,
  t: Translate,
): string | null {
  if (draft.mode !== 'list') return null;
  const path = draft.path.trim();
  if (path.length === 0) {
    return t('model_discovery.error.path_required');
  }
  if (
    !path.startsWith('/') ||
    path.startsWith('//') ||
    path.includes('#') ||
    path.length > 2048
  ) {
    return t('model_discovery.error.path_format');
  }
  const segments = responsePathFromText(draft.responsePathText);
  if (
    segments.length === 0 ||
    segments.length > 8 ||
    segments.some((segment) => segment.length > 128)
  ) {
    return t('model_discovery.error.response_path');
  }
  return null;
}

/**
 * Maps the gateway's exact validation strings (relayed verbatim through
 * ops-api) to operator-readable copy, following the `VendorsPage`
 * discovery-URL precedent. Unknown strings return `null` so callers fall
 * back to the raw `code: message` rendering instead of mistranslating.
 */
export function modelDiscoveryServerError(
  t: Translate,
  cause: unknown,
): string | null {
  if (!(cause instanceof OpsApiError)) return null;
  const message = cause.message;
  switch (message) {
    case 'model_discovery.path must be an origin-relative absolute path starting with one /':
    case 'model_discovery.path must not contain a URL fragment':
    case 'model_discovery.path must be a non-empty string of at most 2048 characters':
      return t('model_discovery.error.path_format');
    case 'model_discovery.path must resolve to the upstream endpoint origin':
      return t('model_discovery.error.path_origin');
    case 'endpoint_url must be an absolute URL when model_discovery.mode is list':
      return t('model_discovery.error.endpoint_absolute');
    case 'model_discovery.response_path must contain 1 to 8 non-empty JSON object keys':
      return t('model_discovery.error.response_path');
    case "model_discovery.auth must be 'anthropic', 'bearer', or 'google-api-key'":
      return t('model_discovery.error.auth');
    case "model_discovery.mode must be 'compatible', 'list', or 'manual'":
      return t('model_discovery.error.mode');
    case 'manual model discovery requires a non-empty models array':
      return t('model_discovery.error.manual_models_required');
    case 'manual model discovery requires an explicit protocol or protocol-classified channel':
      return t('model_discovery.error.manual_protocol_required');
    case 'limit must be an integer between 1 and 200':
      return t('model_discovery.error.limit_range');
    default:
      break;
  }
  if (message.startsWith('model_discovery.mode ') && message.endsWith('does not accept list configuration fields')) {
    return t('model_discovery.error.mode_fields');
  }
  if (message.startsWith('models must contain at most ')) {
    return t('model_discovery.error.models_limit');
  }
  if (message.startsWith('upstream model id must contain ')) {
    return t('model_discovery.error.models_limit');
  }
  if (message.includes('model list exceeds') && message.endsWith('items')) {
    return t('model_discovery.error.list_exceeds');
  }
  return null;
}

interface ModelDiscoveryFieldsProps {
  readonly draft: ModelDiscoveryDraft;
  readonly onChange: (next: ModelDiscoveryDraft) => void;
  readonly disabled?: boolean;
  /** Prefixes element ids and testids, e.g. `upstream-wizard-discovery`. */
  readonly idPrefix: string;
}

export function ModelDiscoveryFields({
  draft,
  onChange,
  disabled = false,
  idPrefix,
}: ModelDiscoveryFieldsProps): JSX.Element {
  const t = useT();
  const modeOptions: readonly SelectOption[] = MODEL_DISCOVERY_MODES.map((mode) => ({
    value: mode,
    label: t(`model_discovery.mode.${mode}`),
  }));
  const authOptions: readonly SelectOption[] = LIST_AUTH_VALUES.map((auth) => ({
    value: auth,
    label: auth,
  }));

  return (
    <div className="flex flex-col gap-4">
      <FormField label={t('model_discovery.mode.label')} fieldId={`${idPrefix}-mode`}>
        <Select
          id={`${idPrefix}-mode`}
          value={draft.mode}
          onValueChange={(value) => {
            const mode = MODEL_DISCOVERY_MODES.find((candidate) => candidate === value);
            if (mode === undefined) return;
            onChange({ ...draft, mode });
          }}
          options={modeOptions}
          disabled={disabled}
          ariaLabel={t('model_discovery.mode.label')}
          testId={`${idPrefix}-mode`}
        />
      </FormField>
      {draft.mode === 'list' ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-testid={`${idPrefix}-list-fields`}>
          <FormField
            label={t('model_discovery.path.label')}
            fieldId={`${idPrefix}-path`}
            hint={t('model_discovery.path.hint')}
            required
          >
            <Input
              id={`${idPrefix}-path`}
              type="text"
              value={draft.path}
              onChange={(e) => onChange({ ...draft, path: e.currentTarget.value })}
              placeholder="/v1/models"
              disabled={disabled}
              mono
              data-testid={`${idPrefix}-path`}
            />
          </FormField>
          <FormField
            label={t('model_discovery.response_path.label')}
            fieldId={`${idPrefix}-response-path`}
            hint={t('model_discovery.response_path.hint')}
            required
          >
            <Input
              id={`${idPrefix}-response-path`}
              type="text"
              value={draft.responsePathText}
              onChange={(e) => onChange({ ...draft, responsePathText: e.currentTarget.value })}
              placeholder="data"
              disabled={disabled}
              mono
              data-testid={`${idPrefix}-response-path`}
            />
          </FormField>
          <FormField label={t('model_discovery.auth.label')} fieldId={`${idPrefix}-auth`} required>
            <Select
              id={`${idPrefix}-auth`}
              value={draft.auth}
              onValueChange={(value) => {
                const auth = LIST_AUTH_VALUES.find((candidate) => candidate === value);
                if (auth === undefined) return;
                onChange({ ...draft, auth });
              }}
              options={authOptions}
              disabled={disabled}
              ariaLabel={t('model_discovery.auth.label')}
              mono
              testId={`${idPrefix}-auth`}
            />
          </FormField>
        </div>
      ) : (
        <p className="m-0 text-dense text-paper-low">
          {draft.mode === 'manual'
            ? t('model_discovery.mode.manual.hint')
            : t('model_discovery.mode.compatible.hint')}
        </p>
      )}
    </div>
  );
}
